// Food Cost — generatori CSV puri per le esportazioni Fase 1 (dati già
// disponibili: acquisti `stock_in`, anagrafica `foods`, ricette, fornitori).
// Nessun I/O qui: ogni funzione ritorna una stringa CSV (separatore ';', BOM per
// Excel). Il download è gestito dal chiamante (FoodCostPage). Testabili.

import {
  computeRecipeCost,
  diagnoseRecipeCost,
  computeDishEconomics,
  convertQty,
  type Recipe,
  type RecipeCostContext,
} from '@trackeat/shared';
import { purchaseAmount, purchaseDate, type PurchaseLine } from './foodCostPurchases.js';

const cell = (s: string): string => (/[;"\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s);
const n2 = (n: number | null | undefined): string => (n == null || !Number.isFinite(n) ? '' : n.toFixed(2));
const pct = (n: number | null | undefined): string => (n == null || !Number.isFinite(n) ? '' : `${n.toFixed(1)}%`);

export function toCsv(head: string[], rows: (string | number | null | undefined)[][]): string {
  const lines = [head.map((h) => cell(String(h))).join(';')];
  for (const r of rows) lines.push(r.map((x) => cell(x == null ? '' : String(x))).join(';'));
  return '﻿' + lines.join('\n');
}

export interface ExpFood {
  id: string; name: string; default_uom: string | null;
  last_unit_cost: number | null; prev_unit_cost: number | null;
  last_unit_cost_uom?: string | null;
  last_unit_cost_currency?: string | null;
  last_unit_cost_at: string | null; supplier_name?: string | null;
}
export type ExpStockIn = PurchaseLine;

const variationPct = (cur: number | null, prev: number | null): number | null =>
  cur != null && prev != null && prev !== 0 ? ((cur - prev) / prev) * 100 : null;

const monthKey = (iso: string): string => (iso ?? '').slice(0, 7); // YYYY-MM
const lineSpend = (e: ExpStockIn): number => purchaseAmount(e) ?? 0;

// 1) Food cost per piatto (% + margine + stato).
// T-450 F1: la % e il margine passano da computeDishEconomics (IVA SCORPORATA
// dal prezzo di vendita lordo + porzioni giuste, NIENTE `?? cost`). Colonne
// aggiuntive "IVA %" e "Prezzo netto €" così il CSV è verificabile a mano.
export function csvFoodCostPerRecipe(
  recipes: Recipe[], ctx: RecipeCostContext, defaultVatPct = 10, blockedRecipeIds: ReadonlySet<string> = new Set(),
): string {
  const head = ['Ricetta', 'Costo €', 'Costo/porzione €', 'Prezzo vendita €', 'IVA %', 'Prezzo netto €', 'Food cost %', 'Margine €', 'Stato'];
  const rows = recipes.filter((r) => !r.archived_at).map((r) => {
    const res = computeRecipeCost(r, ctx);
    const vat = r.vat_rate ?? defaultVatPct;
    // costPerPortion SENZA fallback al costo pieno: se mancano le porzioni,
    // food cost %/margine restano vuoti (mai un numero falsato).
    const costPerPortion = res.status === 'ok' && !blockedRecipeIds.has(r.id) ? res.costPerPortion : null;
    const econ = computeDishEconomics({
      costPerPortion, sellingPriceCentsGross: r.selling_price_cents ?? null, vatRatePct: vat,
    });
    if (res.status !== 'ok') {
      return [r.name, '', '', n2(econ.sellingGross), vat, n2(econ.sellingNet), '', '', 'incompleto'];
    }
    return [r.name, n2(res.cost), costPerPortion != null ? n2(costPerPortion) : '', n2(econ.sellingGross), vat, n2(econ.sellingNet), pct(econ.foodCostPct), n2(econ.marginNet), blockedRecipeIds.has(r.id) ? 'Costo da verificare' : costPerPortion == null ? 'Porzioni mancanti' : 'completo'];
  });
  return toCsv(head, rows);
}

// 2) Listino costi materia prima + variazione.
export function csvListinoCosti(foods: ExpFood[]): string {
  const head = ['Alimento', 'UdM', 'Costo attuale', 'Costo precedente confrontabile', 'Variazione %', 'Ultimo aggiornamento', 'Fornitore', 'Valuta'];
  const rows = foods
    .filter((f) => f.last_unit_cost != null)
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((f) => [f.name, f.last_unit_cost_uom ?? '', n2(f.last_unit_cost), n2(f.prev_unit_cost), pct(variationPct(f.last_unit_cost, f.prev_unit_cost)), (f.last_unit_cost_at ?? '').slice(0, 10), f.supplier_name ?? '', f.last_unit_cost_currency ?? 'EUR']);
  return toCsv(head, rows);
}

// 3) Storico prezzi & variazioni (una riga per osservazione, per alimento nel tempo).
export function csvStoricoPrezzi(stockIn: ExpStockIn[], foodName: (id: string) => string): string {
  const byFood = new Map<string, ExpStockIn[]>();
  for (const e of stockIn) {
    if (e.unit_cost == null) continue;
    (byFood.get(e.food_id) ?? byFood.set(e.food_id, []).get(e.food_id)!).push(e);
  }
  const head = ['Alimento', 'Data documento', 'UdM', 'Valuta', 'Costo', 'Costo precedente', 'Variazione %'];
  const rows: (string | number | null)[][] = [];
  for (const [foodId, evs] of byFood) {
    evs.sort((a, b) => (purchaseDate(a) ?? '').localeCompare(purchaseDate(b) ?? ''));
    let prev: number | null = null;
    let previousUom: string | null = null;
    let previousCurrency: string | null = null;
    for (const e of evs) {
      const comparable = previousUom != null && previousUom === e.uom && previousCurrency === (e.currency ?? 'EUR');
      rows.push([foodName(foodId), purchaseDate(e) ?? 'Data documento mancante', e.uom ?? '', e.currency ?? 'EUR', n2(e.unit_cost), comparable ? n2(prev) : '', comparable ? pct(variationPct(e.unit_cost, prev)) : '']);
      prev = e.unit_cost;
      previousUom = e.uom ?? null;
      previousCurrency = e.currency ?? 'EUR';
    }
  }
  return toCsv(head, rows.sort((a, b) => String(a[0]).localeCompare(String(b[0])) || String(a[1]).localeCompare(String(b[1]))));
}

// 4) Report acquisti: spesa per fornitore × mese.
export function csvReportAcquisti(stockIn: ExpStockIn[], supplierName: (id: string | null) => string): string {
  const agg = new Map<string, { supplier: string; month: string; n: number; spend: number; missing: number }>();
  for (const e of stockIn) {
    const month = purchaseDate(e) ? monthKey(purchaseDate(e)!) : 'Data documento mancante';
    const key = `${e.supplier_id ?? '—'}|${month}`;
    const cur = agg.get(key) ?? { supplier: supplierName(e.supplier_id), month, n: 0, spend: 0, missing: 0 };
    const amount = purchaseAmount(e);
    cur.n += 1; cur.spend += amount ?? 0; cur.missing += amount == null ? 1 : 0;
    agg.set(key, cur);
  }
  const head = ['Fornitore', 'Mese documento', 'Righe', 'Importo noto €', 'Righe senza importo EUR'];
  const rows = [...agg.values()].sort((a, b) => b.spend - a.spend).map((x) => [x.supplier, x.month, x.n, x.n === x.missing ? '' : n2(x.spend), x.missing]);
  return toCsv(head, rows);
}

// 5) Analisi ABC ingredienti (Pareto: il 20% che fa l'80% della spesa).
export function csvAbcIngredienti(stockIn: ExpStockIn[], foodName: (id: string) => string): string {
  const spend = new Map<string, number>();
  for (const e of stockIn) spend.set(e.food_id, (spend.get(e.food_id) ?? 0) + lineSpend(e));
  const sorted = [...spend.entries()].filter(([, s]) => s > 0).sort((a, b) => b[1] - a[1]);
  const total = sorted.reduce((s, [, v]) => s + v, 0) || 1;
  const head = ['Alimento', 'Spesa €', '% sul totale', '% cumulata', 'Classe'];
  let cum = 0;
  const rows = sorted.map(([id, s]) => {
    const share = (s / total) * 100;
    const cls = cum < 80 ? 'A' : cum < 95 ? 'B' : 'C';
    cum += share;
    return [foodName(id), n2(s), pct(share), pct(cum), cls];
  });
  return toCsv(head, rows);
}

// 6) Valorizzazione magazzino. Quando il projector fornisce il valore
// trasportato dei lotti, quello è autorevole: usare l'ultimo prezzo di acquisto
// su un semilavorato/prodotto finito ricreerebbe un valore contabile inventato.
export function csvValorizzazione(
  giacenza: ReadonlyMap<string, number>,
  foods: ExpFood[],
  carriedValueByFood?: ReadonlyMap<string, number>,
): string {
  const costInStockUom = (food: ExpFood): number | null => {
    if (
      food.last_unit_cost == null
      || !Number.isFinite(food.last_unit_cost)
      || food.last_unit_cost < 0
      || !food.default_uom
      || !food.last_unit_cost_uom
    ) {
      return null;
    }
    const costUnitsPerStockUnit = convertQty(
      1,
      food.default_uom,
      food.last_unit_cost_uom,
    );
    return costUnitsPerStockUnit == null
      ? null
      : food.last_unit_cost * costUnitsPerStockUnit;
  };
  const head = [
    'Alimento',
    'Giacenza',
    'UdM giacenza',
    'Costo unitario €',
    'UdM costo',
    'Valore €',
  ];
  const rows = foods
    .map((f) => {
      const q = giacenza.get(f.id) ?? 0;
      const hasCarriedValue = carriedValueByFood?.has(f.id) === true;
      const carriedValue = hasCarriedValue
        ? carriedValueByFood?.get(f.id) ?? null
        : null;
      const fallbackCost = hasCarriedValue ? null : costInStockUom(f);
      const cost = hasCarriedValue
        ? q !== 0 && carriedValue != null
          ? carriedValue / q
          : null
        : fallbackCost;
      const value = hasCarriedValue
        ? carriedValue
        : cost == null
          ? null
          : q * cost;
      return { f, q, cost, value };
    })
    .filter((x) => x.q !== 0 || x.f.last_unit_cost != null)
    .sort((a, b) => (b.value ?? 0) - (a.value ?? 0))
    .map(({ f, q, cost, value }) => [
      f.name,
      n2(q),
      f.default_uom ?? '',
      n2(cost),
      cost == null ? '' : f.default_uom ?? '',
      n2(value),
    ]);
  return toCsv(head, rows);
}

// 7) Alert variazione prezzo (oltre soglia %).
export function csvAlertPrezzo(foods: ExpFood[], thresholdPct = 10): string {
  const head = ['Alimento', 'Costo precedente €', 'Costo attuale €', 'Variazione %', 'Ultimo aggiornamento'];
  const rows = foods
    .map((f) => ({ f, v: variationPct(f.last_unit_cost, f.prev_unit_cost) }))
    .filter((x) => x.v != null && Math.abs(x.v) >= thresholdPct)
    .sort((a, b) => Math.abs(b.v!) - Math.abs(a.v!))
    .map(({ f, v }) => [f.name, n2(f.prev_unit_cost), n2(f.last_unit_cost), pct(v), (f.last_unit_cost_at ?? '').slice(0, 10)]);
  return toCsv(head, rows);
}

// 8) Schede tecniche di costo: distinta base con costo per riga + incidenza %.
export function csvSchedeTecniche(
  recipes: Recipe[], foodName: (id: string) => string, ctx: RecipeCostContext,
): string {
  const head = ['Ricetta', 'Ingrediente o semilavorato', 'Quantità', 'UdM', 'Quantità valorizzata', 'UdM costo', 'Costo unitario €', 'Costo riga €', 'Incidenza %', 'Stato'];
  const rows: (string | number | null)[][] = [];
  for (const r of recipes.filter((x) => !x.archived_at)) {
    const diagnosis = diagnoseRecipeCost(r, ctx);
    for (const line of diagnosis.lines) {
      const inc = diagnosis.severity === 'green' && line.lineCost != null && diagnosis.partialCost > 0
        ? line.lineCost / diagnosis.partialCost * 100 : null;
      rows.push([r.name, line.kind === 'sub' ? ctx.recipesById.get(line.at)?.name ?? line.at : foodName(line.at),
        line.qty ?? '', line.uom ?? '', line.converted?.qty ?? line.qty ?? '', line.converted?.uom ?? line.uom ?? '',
        n2(line.unitCost), n2(line.lineCost), pct(inc), line.resolved ? (diagnosis.severity === 'green' ? 'Completo' : 'Ricetta parziale') : 'Costo non disponibile']);
    }
  }
  return toCsv(head, rows);
}
